import Vue from 'vue';
import VueAxios from 'vue-axios';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import VueMask from 'vue-the-mask';
import VueMoney from 'v-money';

// Recover from stale chunk URLs after a deploy by forcing a single page reload.
//
// The loop-breaker lives in the URL (a `__chunk_reload` query marker), NOT in
// Web Storage. Storage-denied browsers (FB/IG in-app webviews, strict privacy)
// throw on `sessionStorage` access, and even a safe in-memory fallback is wiped
// by the reload — so a storage-based flag can't survive the very reload it's
// meant to gate, and a genuinely-missing chunk would spin into an infinite
// reload loop. A URL query param always survives a reload with no storage at
// all, guaranteeing at most one recovery reload (#5873).
(function () {
    var RELOAD_KEY = '__chunk_reload';

    function isChunkError(value) {
        return value && /ChunkLoadError|Loading chunk \d+ failed|Loading chunk \S+ failed/i.test(String(value));
    }

    // Did THIS page arrive via a recovery reload? Read it from the URL, then
    // strip the marker immediately so the address bar (and anything that logs
    // the URL, e.g. analytics) stays clean. Because the marker survived the
    // reload, `alreadyReloaded` is true here and we will never reload again —
    // no loop, regardless of storage availability.
    var alreadyReloaded = false;
    try {
        var current = new URL(window.location.href);
        if (current.searchParams.has(RELOAD_KEY)) {
            alreadyReloaded = true;
            current.searchParams.delete(RELOAD_KEY);
            window.history.replaceState(window.history.state, document.title, current.toString());
        }
    } catch (e) {
        // URL API unavailable (very old webview): fall through. Worst case is one
        // extra reload, still not an infinite loop for the common transient case.
    }

    function recoverFromChunkError() {
        if (alreadyReloaded) {
            return; // already retried once — stop, never loop
        }
        alreadyReloaded = true;
        try {
            var u = new URL(window.location.href);
            u.searchParams.set(RELOAD_KEY, '1');
            window.location.replace(u.toString());
        } catch (e) {
            // No URL API (ancient webview) means we have no reload-surviving place
            // to record the marker, and storage may be denied too. Reloading here
            // would be unguarded and could loop, so we deliberately do NOT reload —
            // a broken page the user can manually refresh beats an infinite loop.
        }
    }

    window.addEventListener('error', function (event) {
        if (isChunkError(event.error || event.message)) {
            recoverFromChunkError();
        }
    });

    window.addEventListener('unhandledrejection', function (event) {
        var reason = event.reason;
        if (isChunkError(reason && (reason.message || reason))) {
            recoverFromChunkError();
        }
    });
}());


// Placeholder / missing keys must not abort the rest of this file (axios, Vue
// plugins). Offline CI and local Cypress use dummy Mix Bugsnag values.
var bugsnagStarted = false;
try {
    if (window.BUGSNAG_API_KEY) {
        Bugsnag.start({
            apiKey: window.BUGSNAG_API_KEY,
            releaseStage: window.BUGSNAG_RELEASE_STAGE,
            plugins: [new BugsnagPluginVue()]
        });

        const bugsnagVue = Bugsnag.getPlugin('vue');
        if (bugsnagVue) {
            bugsnagVue.installVueErrorHandler(Vue);
        }
        window.Bugsnag = Bugsnag;
        bugsnagStarted = true;
    } else {
        window.Bugsnag = { notify: function () {}, leaveBreadcrumb: function () {} };
    }
} catch (e) {
    console.warn('Bugsnag failed to start; continuing without it.', e);
    window.Bugsnag = { notify: function () {}, leaveBreadcrumb: function () {} };
}

// ============= BUGSNAG ↔ LOGROCKET INTEGRATION =============
// Defensive accessor: Safari + webpack code-split chunks can alias `window` to an
// undefined closure variable. `typeof` check guards before any property access.
var getLR = function() { return (typeof window !== 'undefined' && window.LogRocket) || null; };

if (bugsnagStarted) {
    // Attach LogRocket session URL to every Bugsnag error event (eagerly when available)
    (function() {
        var eagerLR = getLR();
        if (eagerLR && typeof eagerLR.getSessionURL === 'function') {
            eagerLR.getSessionURL(function(sessionURL) {
                window.__logRocketSessionURL = sessionURL;
                Bugsnag.addMetadata('logrocket', { sessionURL: sessionURL });
            });
        }
    }());

    // Tag LogRocket timeline when Bugsnag captures an error + fallback metadata attach
    Bugsnag.addOnError(function(event) {
        // Fallback: if getSessionURL resolved after initial addMetadata but before this error
        if (window.__logRocketSessionURL && !event.getMetadata('logrocket')) {
            event.addMetadata('logrocket', { sessionURL: window.__logRocketSessionURL });
        }

        // If session URL still not available, try to grab it now
        var lr2 = getLR();
        if (!window.__logRocketSessionURL && lr2 && typeof lr2.getSessionURL === 'function') {
            lr2.getSessionURL(function(sessionURL) {
                window.__logRocketSessionURL = sessionURL;
                Bugsnag.addMetadata('logrocket', { sessionURL: sessionURL });
            });
        }

        var error = event.errors[0];
        if (error) {
            var trackData = {
                errorClass: error.errorClass,
                errorMessage: error.errorMessage
            };
            if (window.BUGSNAG_DASHBOARD_URL) {
                trackData.bugsnagSearchUrl = window.BUGSNAG_DASHBOARD_URL +
                    '/errors?filters[search][0]=' + encodeURIComponent(error.errorClass + ' ' + error.errorMessage);
            }
            var lrTrack = getLR();
            if (lrTrack && typeof lrTrack.track === 'function') {
                lrTrack.track('bugsnag_error', trackData);
            }
        }
    });
}
// ============= END BUGSNAG ↔ LOGROCKET INTEGRATION =============

window._ = require('lodash');

Vue.use(require('./filters').default);

/**
 * We'll load jQuery and the Bootstrap jQuery plugin which provides support
 * for JavaScript based Bootstrap features such as modals and tabs. This
 * code may be modified to fit the specific needs of your application.
 */

try {
    window.Popper = require('popper.js').default;
} catch (e) {}

/**
 *
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

window.axios = require('axios');

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

let token = document.head.querySelector('meta[name="csrf-token"]');
    if (token) {
        window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
    }

Vue.use(VueAxios, window.axios);


Vue.use(VueMask);
Vue.use(VueMoney);

// ============= LOGROCKET ERROR TRACKING =============
// Track JavaScript errors
window.addEventListener('error', function(e) {
    getLR()?.track?.('javascript_error', {
        message: e.message,
        file: e.filename,
        line: e.lineno,
        column: e.colno
    });
});

// Track unhandled promise rejections
window.addEventListener('unhandledrejection', function(e) {
    getLR()?.track?.('promise_rejection', {
        reason: String(e.reason)
    });
});

// Transparent CSRF recovery + API error tracking.
//
// The CSRF token is baked into the page once at render (<meta name="csrf-token">)
// and set as an axios default. After the 120-minute session lifetime, a deploy,
// or a server-side session reset, that token goes stale and the next POST fails
// with 419 — which, in the v4 signup SPA, silently dead-ends the user mid-funnel
// (#5875). On a 419 we fetch a fresh token and replay the original request
// exactly once; this covers all call sites (validate-step, resume-step,
// user-behavior) from one place.
//
// This interceptor is deliberately SCOPED to the unauthenticated signup surface:
// any leadgen signup endpoint (`/lg-signup...` — the v4 step calls this fix
// targets, and by extension the v2 and sale/account submit POSTs) plus the
// `/user-behavior` beacon. bootstrap.js also loads on authenticated flows, where
// fetching a fresh token starts a new *guest* session; replaying an auth-
// protected request under it would follow the login redirect and let axios
// resolve the login HTML as a bogus success. Limiting recovery to the signup
// surface avoids that regression. Replaying a signup POST is safe because a 419
// is rejected by VerifyCsrfToken before the controller runs, so the original
// request had no side effect (no double sale/account).
//
// The token-refresh GET itself is excluded so a 419 on it (e.g. an edge
// proxy/WAF, not the framework) can never recurse into another refresh.
const CSRF_RECOVERABLE = /\/(lg-signup|user-behavior)\b/;
const CSRF_TOKEN_ENDPOINT = /\/lg-signup\/v4\/csrf-token\b/;

window.axios.interceptors.response.use(
    response => response,
    async error => {
        const status = error.response?.status;
        const config = error.config;
        const url = config?.url || '';

        if (status === 419 && config && !config._csrfRetried
            && CSRF_RECOVERABLE.test(url) && !CSRF_TOKEN_ENDPOINT.test(url)) {
            config._csrfRetried = true;

            try {
                const { data } = await window.axios.get('/lg-signup/v4/csrf-token');
                const freshToken = data?.token;

                if (freshToken) {
                    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = freshToken;
                    config.headers = config.headers || {};
                    config.headers['X-CSRF-TOKEN'] = freshToken;

                    // Keep the baked meta tag in sync so any non-axios reader
                    // (or a later interceptor pass) sees the fresh token too.
                    const meta = document.head.querySelector('meta[name="csrf-token"]');
                    if (meta) meta.setAttribute('content', freshToken);

                    getLR()?.track?.('csrf_token_refreshed', {
                        url: config.url,
                        method: config.method,
                    });

                    // Replay once. If this still 419s, _csrfRetried is set, so we
                    // fall through to the rejection below instead of looping.
                    return window.axios(config);
                }
            } catch (refreshError) {
                getLR()?.track?.('csrf_token_refresh_failed', {
                    url: config.url,
                    method: config.method,
                    message: refreshError?.message,
                });
                // Fall through to the api_error tracking + rejection below.
            }
        }

        if (status >= 400) {
            getLR()?.track?.('api_error', {
                url: config?.url,
                method: config?.method,
                status,
                message: error.response?.data?.message || error.message
            });
        }

        return Promise.reject(error);
    }
);
// ============= END LOGROCKET ERROR TRACKING =============

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */